'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

type KeyboardShortcutProps = {
  children: string;
} & React.HTMLAttributes<HTMLSpanElement>;

// Define special keys that should be treated as a single unit
const SPECIAL_KEYS = ['Esc', 'Alt', 'Ctrl', 'Tab'];

const KeyboardShortcut = ({
  ref,
  className,
  children,
  ...props
}: KeyboardShortcutProps & { ref?: React.RefObject<HTMLSpanElement | null> }) => {
  // Parse the shortcut string to handle special keys
  const parseShortcut = (shortcut: string) => {
    const tokens: string[] = [];
    let remaining = shortcut;

    // Check for special keys first
    while (remaining.length > 0) {
      let matched = false;

      // Try to match special keys
      for (const key of SPECIAL_KEYS) {
        if (remaining.startsWith(key)) {
          tokens.push(key);
          remaining = remaining.slice(key.length);
          matched = true;
          break;
        }
      }

      // If no special key matched, take the next character
      if (!matched) {
        const char = remaining.charAt(0); // Using charAt which returns an empty string if out of bounds
        if (char !== ' ') {
          // Skip spaces
          tokens.push(char);
        }
        remaining = remaining.slice(1);
      }
    }

    return tokens;
  };

  const tokens = parseShortcut(children);

  return (
    <span
      ref={ref}
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    >
      <div className="flex items-center gap-[2px]">
        {tokens.map((token, index) => {
          // Check if this token is a special key
          const isSpecialKey = SPECIAL_KEYS.includes(token);

          return (
            <span
              key={`shortcut-${children}-${index}`}
              className={cn(
                token === '↵' ? 'mt-px' : undefined,
                // Apply special styling for special keys - normal letter spacing instead of wide
                isSpecialKey ? 'tracking-normal' : undefined,
              )}
            >
              {token}
            </span>
          );
        })}
      </div>
    </span>
  );
};
KeyboardShortcut.displayName = 'KeyboardShortcut';

export { KeyboardShortcut };
